<template>
    <form @submit.prevent="submit">
        <div class="p-6 mb-6 shadow-md rounded-md">
            <div class="text-2xl leading-6 flex justify-between items-center mb-4">
                <h3 class="font-bold">{{ stepTitle }}</h3>
                <p>Step {{ step }} of 5</p>
            </div>

            <div class="divide-y divide-dark">
                <div class="pb-6">
                    <h4 class="text-lg leading-6 font-bold mb-2.5">Invite manually</h4>
                    <p class="mb-6">Send people an invitation by copying the survey link below and sending it to
                        respondents:</p>
                    <div v-if="!useCustomUrl" class="flex items-center mb-6">
                        <p class="text-lg">{{
                                route('tenant.survey.fill.token', {
                                    tenant: surveyModel.tenant_id,
                                    token: surveyModel.token
                                })
                            }}</p>
                        <button @click.prevent="copyToClipboard"
                                class="ml-3"
                                aria-label="Copy" title="Copy">
                            <svg class="h-5 w-5" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                <path
                                    d="M320 448v40c0 13.255-10.745 24-24 24H24c-13.255 0-24-10.745-24-24V120c0-13.255 10.745-24 24-24h72v296c0 30.879 25.121 56 56 56h168zm0-344V0H152c-13.255 0-24 10.745-24 24v368c0 13.255 10.745 24 24 24h272c13.255 0 24-10.745 24-24V128H344c-13.2 0-24-10.8-24-24zm120.971-31.029L375.029 7.029A24 24 0 0 0 358.059 0H352v96h96v-6.059a24 24 0 0 0-7.029-16.97z"/>
                            </svg>
                        </button>
                    </div>
                    <jet-button v-if="!useCustomUrl"
                                :bg-dark="true"
                                @click.prevent="toggleCustomUrl">
                        Change URL
                    </jet-button>
                    <div v-else
                         class="flex items-center"> <!-- asta apare la click pe use custom url -->
                        <p class="text-lg">
                            <span>{{
                                    route('tenant.survey.fill.custom_url', {
                                        tenant: surveyModel.tenant_id,
                                        customUrl: ""
                                    })
                                }}/</span>
                            <jet-input v-model="customUrlForm.custom_url"
                                       type="text"
                                       class="py-1"
                                       @keyup.space="replaceSpaceWithDash"
                            />

                        </p>
                        <jet-button
                            v-if="customUrlForm.custom_url !== surveyModel.custom_url || !surveyModel.custom_url"
                            @click.prevent="updateCustomUrl"
                            aria-label="Save" title="Save"
                            :bg-dark="true"
                            class="ml-2"
                        >
                            <svg class="w-5 h-5" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                <path
                                    d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"/>
                            </svg>
                        </jet-button>
                        <jet-button v-else-if="surveyModel.custom_url"
                                    @click.prevent="copyToClipboard"
                                    class="ml-2"
                                    :bg-dark="true"
                                    aria-label="Copy" title="Copy">
                            <svg class="h-5 w-5" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                <path
                                    d="M320 448v40c0 13.255-10.745 24-24 24H24c-13.255 0-24-10.745-24-24V120c0-13.255 10.745-24 24-24h72v296c0 30.879 25.121 56 56 56h168zm0-344V0H152c-13.255 0-24 10.745-24 24v368c0 13.255 10.745 24 24 24h272c13.255 0 24-10.745 24-24V128H344c-13.2 0-24-10.8-24-24zm120.971-31.029L375.029 7.029A24 24 0 0 0 358.059 0H352v96h96v-6.059a24 24 0 0 0-7.029-16.97z"/>
                            </svg>
                        </jet-button>
                        <jet-button @click.prevent="toggleCustomUrl"
                                    class="ml-2"
                                    aria-label="Save"
                                    :bg-dark="true"
                        >
                            Reset
                        </jet-button>
                    </div>
                    <jet-input-error v-if="useCustomUrl" :message="customUrlForm.errors.custom_url"/>
                </div>

                <div class="py-6">
                    <h4 class="text-lg leading-6 font-bold mb-2.5">Invite through the platform</h4>
                    <p class="mb-6">Send an email invitation through our platform</p>
                    <div class="">
                        <div class="grid gap-6 xl:grid-cols-2 mb-6">
                            <label class="block">
                                <span class="block text-lg leading-6 font-bold">
                                    Type the email addresses of the recipients ({{ inputEmailAddressCount }} email addresses)
                                </span>
                                <span class="block mb-2.5">One email address per line</span>
                                <jet-textarea rows="5"
                                              v-model="invitedResponders"
                                              @change="validateEmailAddressArray"
                                              class="block w-full"></jet-textarea>
                                <jet-input-error :message="form.errors.invited_respondents"></jet-input-error>
                            </label>
                            <div class="block">
                                <span class="block text-lg leading-6 font-bold">Upload a CSV file of email addresses</span>
                                <span class="block mb-2.5">Select a file containing your contacts to import.</span>
                                <div @drag.prevent.stop=""
                                    @dragstart.prevent.stop=""
                                    @dragend.prevent.stop=""
                                    @dragenter.prevent.stop="isDragover = true"
                                    @dragleave.prevent.stop="isDragover = false"
                                    @dragover.prevent.stop="isDragover = true"
                                    @drop.prevent.stop="handleDrop"
                                    :class="{
                                        'bg-light' : isDragover,
                                        'bg-white': !isDragover
                                    }"
                                    class="p-7 border border-dark rounded-md">
                                    <div class="space-y-1 text-center my-1">
                                        <div class="text-center">
                                            <svg class="mx-auto h-10 w-10" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                                <path d="M296 384h-80c-13.3 0-24-10.7-24-24V192h-87.7c-17.8 0-26.7-21.5-14.1-34.1L242.3 5.7c7.5-7.5 19.8-7.5 27.3 0l152.2 152.2c12.6 12.6 3.7 34.1-14.1 34.1H320v168c0 13.3-10.7 24-24 24zm216-8v112c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24V376c0-13.3 10.7-24 24-24h136v8c0 30.9 25.1 56 56 56h80c30.9 0 56-25.1 56-56v-8h136c13.3 0 24 10.7 24 24zm-124 88c0-11-9-20-20-20s-20 9-20 20 9 20 20 20 20-9 20-20zm64 0c0-11-9-20-20-20s-20 9-20 20 9 20 20 20 20-9 20-20z"/>
                                            </svg>
                                            <div class="flex justify-center text-sm text-gray-600">
                                                <label for="file-upload"
                                                       class="relative cursor-pointer rounded-md text-lg">
                                                    <span class="underline">Upload a file</span>
                                                    <input id="file-upload"
                                                           name="file-upload"
                                                           type="file"
                                                           accept="text/csv"
                                                           multiple
                                                           @input="handleFilesSelect"
                                                           class="sr-only">
                                                </label>
                                                <p class="pl-1 text-lg">or drag and drop it
                                                    here</p>
                                            </div>
                                            <p class="text-sm text-gray-500">
                                                Select a .csv file from your computer
                                            </p>
                                        </div>
                                        <ol v-if="uploadedFiles.length"
                                            class="pt-4 text-left justify-start">
                                            <li v-for="(file, index) in uploadedFiles"
                                                :key="'file-'+index"
                                                class="leading-5"
                                            >
                                                <font-awesome-icon v-if="file.status !== STATUS_FINISHED"
                                                                   :icon="['fas', 'spinner']"
                                                                   class="mr-2 fa-spin"></font-awesome-icon>
                                                <b>{{ file.name }}</b>
                                                -
                                                <span v-show="file.status === STATUS_PARSING">parsing</span>
                                                <span v-show="file.status === STATUS_VALIDATING">validating</span>
                                                <span v-show="file.status === STATUS_PROCESSING">processing</span>
                                                <template v-if="file.status === STATUS_FINISHED">
                                                    <span class="text-green-500">{{ file.valid }} unique emails</span>
                                                    successfully
                                                    added,
                                                    <span class="dark:text-yellow-300">{{ file.duplicates.length }} duplicate emails</span>
                                                    successfully
                                                    processed,
                                                    <span class="text-red-500"
                                                          :title="'Invalid rows: ' + file.invalidRows.join(',')">{{
                                                            file.invalidRows.length
                                                        }} emails</span>
                                                    failed
                                                </template>
                                            </li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="block">
                            <div class="flex items-center mb-2.5">
                                <h4 class="text-lg leading-6 font-bold">
                                    Customise the text that appears in the invite email
                                </h4>
                                <button @click.prevent="revertToDefaultEmailText"
                                        class="flex items-center justify-between ml-2 px-1 py-1 text-xs font-medium leading-5 transition-colors duration-150 border border-gray-300 bg-light dark:border-gray-500 dark:bg-gray-600 dark:text-gray-200 border-0 focus:outline-none focus:shadow-none rounded-lg"
                                        aria-label="Revert to default" title="Revert to default">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none"
                                         viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                              d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                                    </svg>
                                </button>
                                <button @click.prevent="showEmailPreviewModal"
                                        title="Preview email"
                                        class="ml-auto flex items-center font-bold text-lg leading-6"
                                        aria-label="Preview email"
                                >
                                    <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg"
                                         viewBox="0 0 512 512">
                                        <path
                                            d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/>
                                    </svg>
                                    Preview email
                                </button>
                            </div>
                            <ckeditor :editor="editor"
                                      v-model="form.email_text"
                                      class="block w-full mt-1 text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 form-textarea focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:focus:shadow-outline-gray"
                                      :config="editorConfig"></ckeditor>
                        </div>
                    </div>
                </div>

                <div class="py-6">
                    <h4 class="text-lg leading-6 font-bold mb-2.5">Survey availability</h4>
                    <label class="mb-2 flex items-center text-gray-600 dark:text-gray-400">
                        <jet-radio v-model:checked="scheduledSurvey"
                                   :value="false">
                            Available immediately (the survey will go live after creation process is finished)
                        </jet-radio>
                    </label>
                    <label class="mb-2 flex items-center text-gray-600 dark:text-gray-400">
                        <jet-radio v-model:checked="scheduledSurvey"
                                   :value="true">
                            Scheduled for a specific time (the survey will go live at the specified time, or after
                            creation process is finished, whichever comes last)
                        </jet-radio>
                    </label>
                    <div v-if="scheduledSurvey" class="mt-4">
                        <label class="block lg:w-1/2">
                            <span class="block text-lg font-semibold leading-6 mb-2.5">Date</span>
                            <jet-input type="datetime-local"
                                       v-model="form.live_at"
                                       @change="validateScheduledDatetime"
                                       :min="minScheduledDatetime.format('YYYY-MM-DDTHH:mm')"
                                       class="block w-full"/>
                            <jet-input-error :message="form.errors.live_at"/>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex justify-between">
            <jet-button @click="prevStep" :disabled="form.processing"
                        :bg-dark="true"
            >
                <svg class="mr-2 h-3.5 w-2" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                    <path
                        d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"/>
                </svg>
                Previous step
            </jet-button>
            <jet-button @click="nextStep" :disabled="form.processing || form.hasErrors"
                        class="ml-auto"
                        :bg-dark="true"
            >
                Next step
                <svg class="ml-2 h-3.5 w-2" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                    <path
                        d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"/>
                </svg>
            </jet-button>
        </div>

        <!-- Modal backdrop. This what you want to place close to the closing body tag -->
        <transition enter-active-class="transition ease-out duration-150"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="transition ease-in duration-150"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
        >
            <div
                v-if="emailPreviewModalShown"
                class="fixed inset-0 z-30 flex items-end bg-black bg-opacity-50 sm:items-center sm:justify-center"
            >
                <!-- Modal -->
                <transition enter-active-class="transition ease-out duration-150"
                            enter-from-class="opacity-0 transform translate-y-1/2"
                            enter-to-class="opacity-100"
                            leave-active-class="transition ease-in duration-150"
                            leave-from-class="opacity-100"
                            leave-to-class="opacity-0 transform translate-y-1/2"
                >
                    <div
                        v-if="emailPreviewModalShown"
                        @keydown.esc="closeEmailPreviewModal"
                        class="w-full px-6 py-4 overflow-auto bg-white rounded-t-lg dark:bg-gray-800 sm:rounded-lg sm:m-4 max-w-max max-h-screen"
                        role="dialog"
                        id="modal"
                    >
                        <!-- Remove header if you don't want a close icon. Use modal body to place modal tile. -->
                        <header class="flex justify-end">
                            <button
                                class="inline-flex items-center justify-center w-6 h-6 text-gray-400 transition-colors duration-150 rounded dark:hover:text-gray-200 hover: hover:text-gray-700"
                                aria-label="close"
                                @click.prevent="closeEmailPreviewModal"
                            >
                                <svg
                                    class="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    role="img"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clip-rule="evenodd"
                                        fill-rule="evenodd"
                                    ></path>
                                </svg>
                            </button>
                        </header>
                        <!-- Modal body -->
                        <div class="mt-4 mb-6" v-html="emailPreviewContent"></div>
                        <footer
                            class="flex flex-col items-center justify-end px-6 py-3 -mx-6 -mb-4 space-y-4 sm:space-y-0 sm:space-x-6 sm:flex-row bg-gray-50 dark:bg-gray-800"
                        >
                            <button
                                @click.prevent="closeEmailPreviewModal"
                                class="px-4 py-2 flex items-center text-lg leading-5 rounded-md text-white bg-dark hover:bg-darkh border border-transparent focus:outline-none focus:shadow-none disabled:opacity-25 transition transition-all duration-150"
                            >
                                Close
                            </button>
                        </footer>
                    </div>
                </transition>
            </div>
        </transition>
    </form>
</template>

<script>
import JetButton from "@/Jetstream/Button";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetTextarea from "@/Jetstream/Textarea";
import JetRadio from "@/Jetstream/Radio";
import {useForm} from "@inertiajs/inertia-vue3";
import {computed, inject, ref, watch} from "vue";
import useClipboard from 'vue-clipboard3'
import useEmailAddressImporter from "@/composables/emailAddressImporter";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CKEditor from "@ckeditor/ckeditor5-vue";

export default {
    name: "SurveyEditorStepFour",
    components: {
        JetButton,
        JetInput,
        JetInputError,
        JetTextarea,
        JetRadio,
        ckeditor: CKEditor.component
    },
    emits: ['next-step', 'prev-step'],
    props: {
        surveyModel: {
            type: Object,
            required: true,
        },
        defaultEmailText: {
            type: [String, null],
            required: true,
        },
        step: {
            type: Number,
            required: true
        },
        stepTitle: {
            type: String,
            required: true
        }
    },
    setup(props, {emit}) {
        const moment = inject('moment');

        const form = useForm({
            step: 4,
            next_step: 5,
            email_text: props.surveyModel.email_text || '',
            live_at: props.surveyModel.live_at ? moment(props.surveyModel.live_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DDTHH:mm') : null,
            invited_respondents: props.surveyModel.invited_respondents || [],
        });

        const invitedResponders = computed({
            get: () => {
                return form.invited_respondents.join("\n");
            },
            set: (invitedRespondentsString) => {
                form.invited_respondents = invitedRespondentsString.split('\n');
            }
        });

        const scheduledSurvey = ref(!!props.surveyModel.live_at);

        watch(scheduledSurvey, value => {
            if (!value) {
                form.live_at = null;
            }
        })

        const customUrlForm = useForm({
            custom_url: props.surveyModel.custom_url,
        });

        const useCustomUrl = ref(!!customUrlForm.custom_url);

        const toggleCustomUrl = () => {
            useCustomUrl.value = !useCustomUrl.value;
            if (!useCustomUrl.value) {
                customUrlForm.custom_url = null;
                updateCustomUrl(false);
            }
        }

        const updateCustomUrl = (validateRequired = true) => {
            if (validateRequired) {
                if (!customUrlForm.custom_url) {
                    customUrlForm.errors.custom_url = "Please fill in the custom url before saving";
                    return;
                }
            }
            customUrlForm.put(
                route('tenant.survey.update-custom-url',
                    {tenant: props.surveyModel.tenant_id, survey: props.surveyModel.id}
                )
            );
        }

        const {toClipboard} = useClipboard();

        const $toast = inject('$toast');

        const copyToClipboard = async (custom_url = false) => {
            let urlString;
            try {
                if (custom_url && props.surveyModel.custom_url) {
                    urlString = route('tenant.survey.fill.custom_url', {
                        tenant: props.surveyModel.tenant_id,
                        customUrl: props.surveyModel.custom_url,
                    });
                } else {
                    urlString = route('tenant.survey.fill.token', {
                        tenant: props.surveyModel.tenant_id,
                        token: props.surveyModel.token,
                    });
                }
                await toClipboard(urlString);
                $toast('Copied to clipboard', {type: "success"});
            } catch (e) {
                console.error(e)
            }
        }

        const replaceSpaceWithDash = () => {
            customUrlForm.custom_url = customUrlForm.custom_url.replace(/\s+/g, '-');
        }

        const nextStep = () => {
            form.next_step = 5;
        }

        const prevStep = () => {
            form.next_step = 3;
        }

        const submit = () => {
            const modelLiveAt = props.surveyModel.live_at
                ? moment(props.surveyModel.live_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DDTHH:mm')
                : null;

            if (form.live_at !== modelLiveAt) {
                validateScheduledDatetime();
            }

            form.transform(data => {
                const {step, next_step, email_text, live_at, invited_respondents} = data;

                return {
                    step,
                    next_step,
                    email_text,
                    live_at: live_at ? moment(live_at, 'YYYY-MM-DDTHH:mm').format('YYYY-MM-DD HH:mm:00') : null,
                    invited_respondents: invited_respondents.filter((email) => email.length),
                }
            }).put(
                route('tenant.survey.update', {tenant: props.surveyModel.tenant_id, survey: props.surveyModel.id}),
                {
                    onSuccess: () => {
                        if (form.next_step === 5) {
                            emit('next-step');
                        } else if (form.next_step === 3) {
                            emit('prev-step');
                        }
                    }
                }
            );
        };

        const minScheduledDatetime = moment().add(30, 'minutes');
        const validateScheduledDatetime = () => {
            if (!moment(form.live_at, 'YYYY-MM-DDTHH:mm').isAfter(minScheduledDatetime)) {
                form.errors.live_at = "The survey must be scheduled after "
                    + moment(minScheduledDatetime).format('DD-MM-YYYY HH:mm');
            } else {
                form.errors.live_at = null;
            }
        }

        const revertToDefaultEmailText = () => {
            form.email_text = props.defaultEmailText || '';
        }

        const {parseCSV, validateEmail, validateEmailAddresses} = useEmailAddressImporter();
        const isDragover = ref(false);
        const uploadedFiles = ref([]);

        const handleDrop = ($event) => {
            isDragover.value = false;
            processFiles([...$event.dataTransfer.files]);
        }

        const handleFilesSelect = ($event) => {
            processFiles([...$event.target.files]);
        }

        const STATUS_PARSING = 0;
        const STATUS_VALIDATING = 1;
        const STATUS_PROCESSING = 2;
        const STATUS_FINISHED = 3;

        const processFiles = async (files) => {
            for (const file of files) {
                let uploadedFilesIndex = uploadedFiles.value.push({
                    name: file.name,
                    status: STATUS_PARSING,
                    valid: 0,
                    duplicates: [],
                    invalidRows: []
                }) - 1;
                let parseResult = await parseCSV(file);
                uploadedFiles.value[uploadedFilesIndex].status = STATUS_VALIDATING;

                let validatedEmailAddresses = [];
                parseResult.forEach((row, index) => {
                    let email = row[0];
                    if (validateEmail(email)) {
                        if (form.invited_respondents.includes(email) || validatedEmailAddresses.includes(email)) {
                            uploadedFiles.value[uploadedFilesIndex].duplicates.push(email)
                        } else {
                            validatedEmailAddresses.push(email);
                        }
                    } else if (row[0].length) {
                        uploadedFiles.value[uploadedFilesIndex].invalidRows.push(index + 1);
                    }
                })

                uploadedFiles.value[uploadedFilesIndex].status = STATUS_PROCESSING;
                uploadedFiles.value[uploadedFilesIndex].valid = validatedEmailAddresses.length;

                form.invited_respondents = [...form.invited_respondents, ...validatedEmailAddresses];
                uploadedFiles.value[uploadedFilesIndex].status = STATUS_FINISHED;
            }
        }

        const validateEmailAddressArray = () => {
            let result = validateEmailAddresses(form.invited_respondents);
            form.hasErrors = !result.valid;
            form.errors.invited_respondents = result.error;
        }

        const inputEmailAddressCount = computed(() => {
            return form.invited_respondents.filter((emailAddress) => {
                return emailAddress.length && validateEmail(emailAddress);
            })
                .filter((email, index, emailList) => {
                    return email.length && emailList.indexOf(email) === index
                })
                .length;
        });

        const emailPreviewModalShown = ref(false);

        const showEmailPreviewModal = () => {
            axios.post(route('tenant.survey.invite-email.preview', {
                    tenant: props.surveyModel.tenant_id,
                    survey: props.surveyModel.id
                }), {
                    email_text: form.email_text,
                }
            ).then((result) => {
                emailPreviewContent.value = result.data;
                emailPreviewModalShown.value = true;
            });

        };

        const closeEmailPreviewModal = () => {
            emailPreviewModalShown.value = false;
        };

        const emailPreviewContent = ref('');

        const editor = ClassicEditor;
        const editorConfig = {
            removePlugins: ['imageUpload', 'mediaEmbed'],
            link: {
                addTargetToExternalLinks: true,
                defaultProtocol: 'http://',
            },
            toolbar: {
                removeItems: ['uploadImage', 'mediaEmbed']
            },
        };

        return {
            form,
            invitedResponders,
            customUrlForm,
            useCustomUrl,
            replaceSpaceWithDash,
            copyToClipboard,
            updateCustomUrl,
            toggleCustomUrl,
            nextStep,
            prevStep,
            submit,
            scheduledSurvey,
            minScheduledDatetime,
            validateScheduledDatetime,
            revertToDefaultEmailText,
            uploadedFiles,
            isDragover,
            handleDrop,
            handleFilesSelect,
            STATUS_PARSING,
            STATUS_VALIDATING,
            STATUS_PROCESSING,
            STATUS_FINISHED,
            validateEmailAddressArray,
            inputEmailAddressCount,
            emailPreviewModalShown,
            showEmailPreviewModal,
            closeEmailPreviewModal,
            emailPreviewContent,
            editor,
            editorConfig,
        }
    }
}
</script>

<style scoped>

</style>
